import React from 'react';
import MaintenanceDateTimeInput from '../../components/settings/MaintenanceDateTimeInput';
import { useMaintenanceMode } from '../../context/MaintenanceContext';
import { useWebsiteMaintenance } from '../../../website/context/WebsiteMaintenanceContext';
import { useNotification } from '../../../context/NotificationContext';
import ControlPanelNameCard from './control-panel/ControlPanelNameCard';
import ComingSoonSettingsCard from './coming-soon/ComingSoonSettingsCard';
import AdminAccessCodeSettingsCard from './admin-access-code/AdminAccessCodeSettingsCard';
import PlatformCredentialsTabs from './credentials/PlatformCredentialsTabs';
import '../../components/packages/package-dialogs.css';
import './AppSettings.css';

const AppSettings = () => {
  const { isMaintenanceMode, scheduledStartTime, toggleMaintenanceMode, setAppMaintenance } = useMaintenanceMode();
  const { isWebsiteMaintenance, toggleWebsiteMaintenance } = useWebsiteMaintenance();
  const { showToast } = useNotification();

  const showSaved = () => {
    showToast('App settings updated.', 'success');
  };

  const handleMaintenanceModeToggle = async () => {
    let success;
    if (isMaintenanceMode) {
      success = await setAppMaintenance({ enabled: false, scheduledStartTime: null });
    } else {
      success = await toggleMaintenanceMode(scheduledStartTime || null);
    }
    if (success) showSaved();
  };

  const handleScheduledStartChange = (iso) => {
    setAppMaintenance({ scheduledStartTime: iso }).then((ok) => {
      if (ok) showSaved();
    });
  };

  const handleWebsiteMaintenanceToggle = async () => {
    const success = await toggleWebsiteMaintenance();
    if (success) showSaved();
  };

  return (
    <div className="admin-page app-settings-page">
      <div className="admin-page__header">
        <div>
          <h1 className="admin-page__title">App Settings</h1>
          <p className="admin-page__subtitle">
            Control panel name, coming soon page, admin access code, maintenance modes, and platform email, SMS, and payment credentials.
          </p>
        </div>
      </div>

      <ControlPanelNameCard />

      <ComingSoonSettingsCard />

      <AdminAccessCodeSettingsCard />

      <section className="admin-card app-settings-card">
        <div className="app-settings-row">
          <div className="app-settings-row__text">
            <h3 className="app-settings-row__title">App Maintenance Mode</h3>
            <p className="app-settings-row__desc">
              Temporarily restrict access to the app for all users except administrators.
            </p>
          </div>
          <div className="app-settings-row__action">
            <label className="pkg-dialog-check" htmlFor="app-maintenance-mode">
              <input
                id="app-maintenance-mode"
                type="checkbox"
                checked={isMaintenanceMode}
                onChange={handleMaintenanceModeToggle}
              />
              Enabled
            </label>
          </div>
        </div>

        <div className="app-settings-row app-settings-row--stacked">
          <p className="app-settings-help">
            Scheduled maintenance time (optional) — notice banner before maintenance; also shown on the
            maintenance page. Enter date and time in Central Time (CT). Users will see it in their local timezone.
          </p>
          <MaintenanceDateTimeInput
            value={scheduledStartTime}
            onChange={handleScheduledStartChange}
          />
        </div>

        <div className="app-settings-row">
          <div className="app-settings-row__text">
            <h3 className="app-settings-row__title">Website Maintenance Mode</h3>
            <p className="app-settings-row__desc">
              Temporarily restrict access to the public marketing site for all users except administrators.
            </p>
          </div>
          <div className="app-settings-row__action">
            <label className="pkg-dialog-check" htmlFor="website-maintenance-mode">
              <input
                id="website-maintenance-mode"
                type="checkbox"
                checked={isWebsiteMaintenance}
                onChange={handleWebsiteMaintenanceToggle}
              />
              Enabled
            </label>
          </div>
        </div>
      </section>

      <PlatformCredentialsTabs />
    </div>
  );
};

export default AppSettings;
